import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/auth/verify-reset-token?token=${token}`);
        setTokenValid(response.data.valid);
        setEmail(response.data.email || '');
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF8EF]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#B2560D] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#505631]">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" data-testid="reset-password-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FBF8EF]">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_timesheet-ops-1/artifacts/uwk69pjg_Black-8.png" 
              alt="Abule Imole" 
              className="h-32 w-auto mb-2"
            />
            <p className="text-sm text-[#505631] font-medium">Administration Portal</p>
          </div>

          <div className="bg-white rounded-xl border border-[#E5E2D9] p-8 shadow-sm">
            {!token || !tokenValid ? (
              // Invalid or missing token
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-medium text-[#2B3210] mb-2">
                  Invalid or Expired Link
                </h2>
                <p className="text-sm text-[#505631] mb-6">
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
                <a
                  href="/forgot-password"
                  className="block w-full bg-[#B2560D] text-white py-2.5 rounded-lg hover:bg-[#8A4209] transition-colors duration-200 font-medium text-center"
                  data-testid="request-new-link-button"
                >
                  Request New Link
                </a>
              </div>
            ) : success ? (
              // Success state
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-medium text-[#2B3210] mb-2">
                  Password Reset Successful
                </h2>
                <p className="text-sm text-[#505631] mb-6">
                  Your password has been reset successfully. You can now log in with your new password.
                </p>
                <a
                  href="/login"
                  className="block w-full bg-[#B2560D] text-white py-2.5 rounded-lg hover:bg-[#8A4209] transition-colors duration-200 font-medium text-center"
                  data-testid="go-to-login-button"
                >
                  Go to Login
                </a>
              </div>
            ) : (
              // Reset form
              <>
                <div className="mb-6">
                  <a
                    href="/login"
                    className="inline-flex items-center text-sm text-[#505631] hover:text-[#B2560D] transition-colors"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to login
                  </a>
                </div>

                <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-[#2B3210] mb-2">
                  Reset your password
                </h2>
                {email && (
                  <p className="text-sm text-[#505631] mb-6">
                    Resetting password for <strong>{email}</strong>
                  </p>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A39E8F]" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        data-testid="new-password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="w-full pl-10 pr-10 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A39E8F] hover:text-[#505631]"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-[#A39E8F] mt-1">Must be at least 8 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A39E8F]" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        data-testid="confirm-password-input"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full pl-10 pr-10 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#A39E8F] hover:text-[#505631]"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    data-testid="reset-password-button"
                    disabled={loading || password !== confirmPassword}
                    className="w-full bg-[#B2560D] text-white py-2.5 rounded-lg hover:bg-[#8A4209] transition-colors duration-200 font-medium disabled:opacity-50"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Image with color overlay */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1644035656974-9f979761e6a1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200)',
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            backgroundColor: '#B2560D',
            mixBlendMode: 'multiply',
            opacity: 0.4,
          }}
        />
      </div>
    </div>
  );
};

export default ResetPassword;
